app.factory("patientDocumentService", ["Session", "$filter", "$sanitize", function (Session, $filter, $sanitize) {

    // Helper function to strip trailing OS-appended numbers/parens (e.g. "file.pdf 1" or "file.pdf (2)")
    function getCleanFileName(name) {
        if (!name) return "";
        return name.replace(/(\.[a-zA-Z0-9]{2,5})[\s\(\)-]*\d*[\s\(\)-]*$/, '$1');
    }

    function canChangeFile(file, ownFilePermission, anyFilePermission) {
        if (Session.practice.id !== file.uploaded_by_practice_id) return false;

        const isUploader = `${file.uploaded_by_user_id}` === Session.userId;
        return (isUploader && Session.hasPermission(ownFilePermission)) || Session.hasPermission(anyFilePermission);
    }

    return {
        // --- VISIBILITY & VIEWING PERMISSIONS ---
        canViewDocument: function (documentType) {
            const practiceType = Session.practice.type;
            const restrictedDocs = ['WORKUP', 'OPNOTE', 'INJECTION_NOTE'];
            const allowedPracticeTypes = ['PRIVATE', 'DHB_PUBLIC', 'THEATRE'];

            if (restrictedDocs.includes(documentType)) {
                return allowedPracticeTypes.includes(practiceType);
            }
            return true;
        },

        getSecurityOptions: function () {
            const options = [
                { value: "GLOBAL", name: "Add to the patient record" },
                { value: "PRACTICE", name: "Visible only to the uploader's practice" },
            ];
            // Only ophthalmologists and registrars can keep files to themselves; the server checks it too.
            if (Session.hasAnyRole(["REGISTRAR", "OPHTHALMOLOGIST"])) {
                options.push({ value: "OWNER", name: "Visible only to the uploader" });
            }
            return options;
        },

        formatSecurityLevel: function (level, userName, practiceName) {
            const levelMap = {
                'GLOBAL': 'Patient record',
                'OWNER': userName,
                'PRACTICE': practiceName
            };
            return levelMap[level] || '';
        },

        // --- MODIFICATION / DELETION PERMISSIONS ---
        // Users may change files they uploaded, and other people's files only with the "anyone's
        // files" permission. The file must come from the practice they are working in.
        canDeleteFile: function (file) {
            return canChangeFile(file, "PATIENT_FILE_DELETE", "PATIENT_FILE_ANY_DELETE");
        },

        canModifyFile: function (file) {
            if (!file.file_upload_date) {
                return `${file.document_created_by_id}` === Session.userId;
            }

            return canChangeFile(file, "PATIENT_FILE_DETAILS_UPDATE", "PATIENT_FILE_ANY_DETAILS_UPDATE");
        },

        // --- HL7 & DATA PARSING ---
        getHl7OverallStatus: function (document) {
            const hl7List = document.hl7_message_summary_list || [];
            const hasStatus = (status) => hl7List.some(item => item.hl7_msg_status === status);

            if (hasStatus('PROCESSING')) return 'PROCESSING';
            if (hasStatus('SENT_FAIL')) return 'SENT_FAIL';
            if (hasStatus('ACK_ERROR') || hasStatus('ACK_REJECTED')) return 'DELIVER_FAIL';
            if (hasStatus('ACK_ACCEPT')) return 'DELIVER_SUCCESS';
            if (hasStatus('SENT_OK')) return 'SENT_OK';

            if (document.email_message_summary_list?.length > 0) return 'EMAIL_ONLY';
            return null;
        },

        buildHl7PopoverSummary: function (document) {
            const hl7List = document.hl7_message_summary_list || [];
            const emailList = document.email_message_summary_list || [];
            const lines = [];

            if (hl7List.length) {
                hl7List.forEach(item => {
                    const practiceName = item.hl7_msg_practice_name
                        ? item.hl7_msg_practice_name
                        : 'EMR';
                    const ediText = item.hl7_msg_edi
                        ? `${practiceName}: ${item.hl7_msg_edi}`
                        : `${practiceName} (EDI not recorded)`;

                    let line;
                    switch (item.hl7_msg_status) {
                        case 'PROCESSING':
                            line = `Processing for ${ediText}`;
                            break;
                        case 'SENT_OK':
                            line = `Sent to ${ediText}`;
                            break;
                        case 'SENT_FAIL':
                            line = `Failed to send to ${ediText} with error ${item.hl7_msg_error}`;
                            break;
                        case 'ACK_ACCEPT':
                            line = `Delivered to ${ediText}`;
                            break;
                        case 'ACK_ERROR':
                        case 'ACK_REJECTED':
                            line = `Failed to deliver to ${ediText} with error ${item.hl7_msg_error}`;
                            break;
                        default:
                            line = `${ediText}`;
                    }

                    lines.push(`${item.hl7_msg_id}: ${line}`);
                });
            }

            if (emailList.length) {
                const emailLines = emailList
                    .map(e => `${e.name} (${e.email})`)
                    .join(', ');
                lines.push(`Email recipients: ${emailLines}`);
            }

            if (lines.length === 0) {
                return { title: "HL7 Status", content: "No status available.", html: true };
            }

            return {
                title: document.document_name || "Delivery Status",
                content: lines.join('<br/>'),
                html: true
            };
        },

        // --- STRING FORMATTERS & TRUNCATORS ---
        truncateWithElipsis: function (text, max) {
            if (!text) return "";
            return text.length > max ? text.slice(0, max - 1) + "\u2026" : text;
        },

        upperCaseFirstLetter: function (text) {
            if (!text) return "";
            if (text.startsWith("oct macula")) return "OCT Macula";
            if (text.startsWith("OCT glaucoma")) return "OCT Glaucoma";
            if (text.startsWith("OCT anterior segment")) return "OCT Anterior Segment";

            const words = text.split(" ");
            return words.map((word) => word[0].toUpperCase() + word.substring(1)).join(" ");
        },

        // --- FILE TYPE CHECKERS ---
        isImageFile: function (item) {
            if (!item) return false;
            item = getCleanFileName(item);
            var type = '|' + item.slice(item.lastIndexOf('.') + 1) + '|';
            var type2 = '|' + item.slice(item.lastIndexOf('/') + 1) + '|';
            return '|jpg|png|jpeg|bmp|gif|'.indexOf(type.toLowerCase()) !== -1 || '|jpg|png|jpeg|bmp|gif|'.indexOf(type2.toLowerCase()) !== -1;
        },

        isPdfFile: function (item) {
            if (!item) return false;
            item = getCleanFileName(item);
            var type = '|' + item.slice(item.lastIndexOf('.') + 1) + '|';
            var type2 = '|' + item.slice(item.lastIndexOf('/') + 1) + '|';
            return '|pdf|'.indexOf(type.toLowerCase()) !== -1 || '|pdf|'.indexOf(type2.toLowerCase()) !== -1;
        },

        isWordFile: function (item) {
            if (!item) return false;
            if (item.includes('text/plain') || item.includes("application/x-tika-ooxml")) return true;
            item = getCleanFileName(item);
            var type = '|' + item.slice(item.lastIndexOf('.') + 1).toLowerCase() + '|';
            var type2 = '|' + item.slice(item.lastIndexOf('/') + 1).toLowerCase() + '|';
            return '|vnd.openxmlformats-officedocument.wordprocessingml.document|docx|doc|'.indexOf(type) !== -1 || '|vnd.openxmlformats-officedocument.wordprocessingml.document|docx|doc|'.indexOf(type2) !== -1;
        },

        isVideoFile: function (item) {
            if (!item) return false;
            item = getCleanFileName(item);
            var type = '|' + item.slice(item.lastIndexOf('.') + 1) + '|';
            var type2 = '|' + item.slice(item.lastIndexOf('/') + 1) + '|';
            return '|avi|mp4|mov|quicktime|'.indexOf(type.toLowerCase()) !== -1 || '|avi|mp4|mov|quicktime|'.indexOf(type2.toLowerCase()) !== -1;
        },

        isTXTFile: function (item) {
            if (!item) return false;
            item = getCleanFileName(item);
            var type = '|' + item.slice(item.lastIndexOf('.') + 1) + '|';
            return '|txt|'.indexOf(type.toLowerCase()) !== -1;
        },

        isFileTypeSupported: function (item) {
            // First try to validate using the native browser MIME type
            if (item.type) {
                if (item.type.includes('text/plain')) return true;
                var type = '|' + item.type.slice(item.type.lastIndexOf('/') + 1) + '|';
                if ('|avi|mp4|quicktime|jpg|png|jpeg|bmp|gif|pdf|vnd.openxmlformats-officedocument.wordprocessingml.document|'.indexOf(type) !== -1) {
                    return true;
                }
            }

            // Fallback: If MIME type is missing due to OS drag & drop issues, manually check the cleaned extension
            let cleanName = getCleanFileName(item.name);
            var ext = '|' + cleanName.slice(cleanName.lastIndexOf('.') + 1).toLowerCase() + '|';
            return '|avi|mp4|mov|quicktime|jpg|png|jpeg|bmp|gif|pdf|docx|doc|txt|'.indexOf(ext) !== -1;
        },

        isFileTooBig: function (item) {
            return item.size > 1000 * 1000 * 200;
        },

        isFileNameValid: function (item) {
            if (!item || !item.name) return false;
            let cleanName = getCleanFileName(item.name);
            const regex = new RegExp(/^[a-zA-Z0-9 ()_.\-\,;'="+$%&@#!<>^?\[\]]+\.[A-Za-z0-9]{2,}$/m);
            return regex.test(cleanName);
        },

        // --- FILE DOWNLOAD & GENERATION HELPERS ---
        executeFileDownload: function (data, filename, mime, bom) {
            var blobData = typeof bom !== "undefined" ? [bom, data] : [data];
            var blob = new Blob(blobData, { type: "application/pdf" });
            if (typeof window.navigator.msSaveBlob !== "undefined") {
                window.navigator.msSaveBlob(blob, filename);
            } else {
                var blobURL = window.URL.createObjectURL(blob);
                var tempLink = document.createElement("a");
                tempLink.style.display = "none";
                tempLink.href = blobURL;
                tempLink.setAttribute("download", filename);
                if (typeof tempLink.download === "undefined") {
                    tempLink.setAttribute("target", "_blank");
                }
                document.body.appendChild(tempLink);
                tempLink.click();
                setTimeout(function () {
                    document.body.removeChild(tempLink);
                    window.URL.revokeObjectURL(blobURL);
                }, 100);
            }
        },

        getGeneratedFileName: function (patient, document_type) {
            var theTime = $filter("date")(new Date(), "yyyy-MM-dd_hmma");
            var rawName = `CatTrax_${document_type}_${theTime}_${patient.firstName}_${patient.lastName}_${patient.dateOfBirth}.pdf`;
            return rawName.replace(/[^a-zA-Z0-9_.-]/g, '_');
        },

        getVideoConfig: function (videoUrl) {
            return {
                preload: "none",
                sources: [{ src: videoUrl, type: "video/mp4" }],
                tracks: [],
                theme: { url: "https://unpkg.com/videogular@2.1.2/dist/themes/default/videogular.css" },
                plugins: { poster: "https://www.cattrax.co.nz/app/build/img/cattrax_logo.png" }
            };
        },

        getFileOptions: function (file_mime_type, fileTypesMap) {
            if (this.isImageFile(file_mime_type) || this.isPdfFile(file_mime_type)) return fileTypesMap.image;
            if (this.isWordFile(file_mime_type)) return fileTypesMap.word;
            if (this.isVideoFile(file_mime_type)) return fileTypesMap.video;
            return fileTypesMap.image;
        },

        // --- PAYLOAD BUILDERS ---
        buildFileUpdateBody: function (selectedFile, newFileItem) {
            let body = {};
            let fileName = `${newFileItem.file.name}.${newFileItem.file.suffix}`;
            // Compared with the full name: file_name is the shortened one the row displays.
            if ((selectedFile.full_name || selectedFile.file_name) !== fileName) body.full_name = fileName;
            if (selectedFile.file_type !== newFileItem.fileType) body.file_type = newFileItem.fileType;
            if (selectedFile.eye !== newFileItem.operative_eye) body.eye = newFileItem.operative_eye;
            if (selectedFile.security_level !== newFileItem.security_level) body.security_level = newFileItem.security_level;
            if (newFileItem.comment) body.comments = [{ comment: newFileItem.comment }];

            // The referring provider details a claim against this referral is made under. Sent only
            // when they actually changed, so an update that did not touch them cannot rewrite what
            // the referral already carries. Sent as a pair, since the server takes them together.
            const currentProviderDetails = (selectedFile.referral_data && selectedFile.referral_data.provider_details) || {};
            const providerId = newFileItem.referral_provider_id || null;
            const referralPeriod = newFileItem.referral_period || null;
            // Only a non-standard period has a length to record; every other one describes its own, and
            // keeping a stale figure against it would put a length on a referral that has none.
            const referralPeriodMonths = referralPeriod === 'NON_STANDARD'
                ? (Number(newFileItem.referral_period_months) || null) : null;

            if ((currentProviderDetails.provider_id || null) !== providerId ||
                    (currentProviderDetails.referral_period || null) !== referralPeriod ||
                    (currentProviderDetails.referral_period_months || null) !== referralPeriodMonths) {
                body.referral_data = {
                    provider_details: {
                        provider_id: providerId,
                        referral_period: referralPeriod,
                        referral_period_months: referralPeriodMonths
                    }
                };
            }

            return body;
        },

        buildAppendPayload: function (doc, patientId, operationNoteList) {
            const documentId = doc.document_id;
            if (doc.document_type === 'INJECTION_NOTE') {
                let eye = "OD/OS";
                if (doc.document_name.endsWith('OD')) eye = "OD";
                else if (doc.document_name.endsWith('OS')) eye = "OS";

                return {
                    operative_eye: eye === "OD/OS" ? "BOTH" : eye,
                    disableSelect: eye !== "OD/OS",
                    operationNote: { injection_note_id: documentId, patient_id: patientId, op_note_od: {}, op_note_os: {} }
                };
            } else if (doc.document_type === 'OPNOTE') {
                let note = operationNoteList.find(t => t.operation_note_id === documentId);
                if (!note) note = { eye: "OD/OS" };
                return {
                    operative_eye: note.eye === "OD/OS" ? "BOTH" : note.eye,
                    disableSelect: note.eye !== "OD/OS",
                    operationNote: { operation_note_id: documentId, patient_id: patientId, op_note_od: { operative_eye: "NO" }, op_note_os: { operative_eye: "NO" } }
                };
            }
            return null;
        },

        convertDateOfConsultation: function (src) {
            const array = src.split('/');
            return `${array[2]}-${array[1]}-${array[0]}`;
        },

        // --- FILE PREVIEW READING ---
        handleFileSelect: function (file, isTXT) {
            var reader = new FileReader();
            if (isTXT) {
                reader.onload = function (loadEvent) {
                    const content = document.querySelector(".content");
                    if (content) content.innerText = loadEvent.target.result;
                };
                reader.readAsText(file);
                return;
            }

            reader.onload = function (loadEvent) {
                var arrayBuffer = loadEvent.target.result;
                if (typeof mammoth !== 'undefined') {
                    mammoth.convertToHtml({ arrayBuffer: arrayBuffer })
                        .then(function (result) {
                            var out = document.getElementById("output");
                            // Mammoth does not sanitise its output (javascript: links survive), and the
                            // document may have been uploaded by another practice.
                            if (out) out.innerHTML = $sanitize(result.value);
                        })
                        .catch(function (error) { console.error("Word preview failed:", error && error.name); });
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };
}]);